import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar, FileSpreadsheet, ClipboardCheck, Calculator, FileText, Printer, ChevronLeft, ChevronRight, Settings2, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 }));

const useMonthYear = () => {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  return { month, year, setMonth, setYear };
};

const MonthYearNav = ({ month, year, setMonth, setYear, testidPrefix }) => (
  <div className="flex items-center gap-1">
    <button data-testid={`${testidPrefix}-prev`} onClick={() => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); }} className="p-1 rounded border border-stone-300 hover:bg-stone-100"><ChevronLeft className="w-3.5 h-3.5" /></button>
    <select data-testid={`${testidPrefix}-month`} value={month} onChange={e => setMonth(parseInt(e.target.value, 10))} className="text-xs border border-stone-300 rounded px-2 py-1 bg-white">
      {MONTHS.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
    </select>
    <select data-testid={`${testidPrefix}-year`} value={year} onChange={e => setYear(parseInt(e.target.value, 10))} className="text-xs border border-stone-300 rounded px-2 py-1 bg-white">
      {[2024, 2025, 2026, 2027].map(y => (<option key={y} value={y}>{y}</option>))}
    </select>
    <button data-testid={`${testidPrefix}-next`} onClick={() => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); }} className="p-1 rounded border border-stone-300 hover:bg-stone-100"><ChevronRight className="w-3.5 h-3.5" /></button>
  </div>
);

// ─────────────────────────────────────────────────────────────────
//  SUB-TAB 1: INDEX 1
// ─────────────────────────────────────────────────────────────────
const IndexTab = () => {
  const { month, year, setMonth, setYear } = useMonthYear();
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('crm_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/hr/payroll/index`, { params: { month, year }, headers })
      .then(r => setData({ rows: r.data.rows || [], totals: r.data.totals || {} }))
      .catch(() => toast.error('Failed to load Index 1'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  return (
    <div data-testid="payroll-index-tab" className="space-y-3">
      <div className="bg-amber-700 text-white px-3 py-2 rounded-t font-bold text-sm flex items-center justify-between">
        <span>{MONTHS[month - 1].toUpperCase()}-{year} PAYMENT INDEX 1</span>
        <MonthYearNav month={month} year={year} setMonth={setMonth} setYear={setYear} testidPrefix="payroll-index" />
      </div>
      <Card className="rounded-t-none">
        <CardContent className="p-0">
          <div className="overflow-auto" style={{ maxHeight: 600 }}>
            <table className="text-[11px] border-collapse w-full" style={{ minWidth: 1400 }}>
              <thead className="sticky top-0 z-10 bg-amber-100">
                <tr>
                  {['SR', 'NAME', 'GROSS', 'EMP PF', 'ESIC', 'PT', 'EMP CONTRIB', 'OTHER ALLOW', 'NET', 'PAID', 'CHARGES', 'BAL', 'EMPLOYER PF', 'EMPLOYER ESIC', 'EMPLOYER CONTRIB', 'REMARK'].map(h => (
                    <th key={h} className="px-2 py-2 text-left font-bold border border-amber-300 text-amber-900 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={16} className="py-8 text-center text-stone-400">Loading…</td></tr>}
                {!loading && data.rows.map((r, i) => (
                  <tr key={r.employeeId || i} className={i % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                    <td className="px-2 py-1 border border-stone-200 text-center font-semibold">{r.srNo}</td>
                    <td className="px-2 py-1 border border-stone-200 font-semibold whitespace-nowrap">{r.name}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right">{fmt(r.gross)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-red-700">{fmt(r.empPf)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-red-700">{fmt(r.esicEmp)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-red-700">{fmt(r.pt)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right font-bold text-red-800">{fmt(r.empContribution)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right">{fmt(r.otherAllowance)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right font-bold text-emerald-700">{fmt(r.netSalary)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right">{fmt(r.paidSalary)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right">{fmt(r.charges)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-blue-700">{fmt(r.balance)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-purple-700">{fmt(r.employerPf)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-purple-700">{fmt(r.esicEmployer)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right font-bold text-purple-800">{fmt(r.employerContribution)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-stone-500 truncate max-w-[150px]" title={r.remark}>{r.remark || ''}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 z-10 bg-amber-200">
                <tr>
                  <td colSpan={2} className="px-2 py-2 font-bold text-amber-900 border border-amber-400">TOTAL</td>
                  <td className="px-2 py-2 text-right font-bold border border-amber-400">{fmt(data.totals.gross)}</td>
                  <td colSpan={3} className="border border-amber-400" />
                  <td className="px-2 py-2 text-right font-bold text-red-800 border border-amber-400">{fmt(data.totals.empContribution)}</td>
                  <td className="px-2 py-2 text-right font-bold border border-amber-400">{fmt(data.totals.otherAllowance)}</td>
                  <td className="px-2 py-2 text-right font-bold text-emerald-800 border border-amber-400">{fmt(data.totals.netSalary)}</td>
                  <td className="px-2 py-2 text-right font-bold border border-amber-400">{fmt(data.totals.paidSalary)}</td>
                  <td className="px-2 py-2 text-right font-bold border border-amber-400">{fmt(data.totals.charges)}</td>
                  <td className="px-2 py-2 text-right font-bold text-blue-800 border border-amber-400">{fmt(data.totals.balance)}</td>
                  <td colSpan={2} className="border border-amber-400" />
                  <td className="px-2 py-2 text-right font-bold text-purple-800 border border-amber-400">{fmt(data.totals.employerContribution)}</td>
                  <td className="border border-amber-400" />
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
//  SUB-TAB 2: PRESENTY (read-only mirror of attendance)
// ─────────────────────────────────────────────────────────────────
const PresentyTab = () => {
  const { month, year, setMonth, setYear } = useMonthYear();
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const days = useMemo(() => new Date(year, month, 0).getDate(), [month, year]);
  const dayNumbers = useMemo(() => Array.from({ length: days }, (_, i) => i + 1), [days]);
  const token = localStorage.getItem('crm_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, attRes] = await Promise.all([
        axios.get(`${API}/hr/attendance-employees`, { headers }),
        axios.get(`${API}/hr/monthly-attendance`, { params: { month, year }, headers }),
      ]);
      setEmployees(empRes.data.employees || []);
      setAttendance(attRes.data.records || attRes.data.attendance || []);
    } catch {
      toast.error('Failed to load Presenty');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const attMap = useMemo(() => {
    const m = {};
    attendance.forEach(a => { m[a.employeeId || a.employeeName] = a.days || {}; });
    return m;
  }, [attendance]);

  const STATUS_COLORS = {
    P: { bg: '#dcfce7', fg: '#166534' },
    H: { bg: '#fef9c3', fg: '#854d0e' },
    A: { bg: '#fee2e2', fg: '#991b1b' },
    U: { bg: '#fed7aa', fg: '#9a3412' },
    SL: { bg: '#dbeafe', fg: '#1e40af' },
    PL: { bg: '#e9d5ff', fg: '#6b21a8' },
    WO: { bg: '#f1f5f9', fg: '#334155' },
    HO: { bg: '#cffafe', fg: '#155e75' },
    L: { bg: '#fde68a', fg: '#92400e' },
  };

  return (
    <div data-testid="payroll-presenty-tab" className="space-y-3">
      <div className="bg-emerald-700 text-white px-3 py-2 rounded-t font-bold text-sm flex items-center justify-between">
        <span>PRESENTY · {MONTHS[month - 1].toUpperCase()} {year} (read-only — synced from Attendance)</span>
        <MonthYearNav month={month} year={year} setMonth={setMonth} setYear={setYear} testidPrefix="payroll-presenty" />
      </div>
      <Card className="rounded-t-none">
        <CardContent className="p-0">
          <div className="overflow-auto border border-stone-200" style={{ maxHeight: 600 }}>
            <table className="text-[11px] border-collapse" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky top-0 z-20">
                <tr className="bg-emerald-700 text-white">
                  <th className="px-2 py-2 text-center font-bold border border-emerald-900 sticky left-0 z-30 bg-emerald-700" style={{ width: 38 }}>SR</th>
                  <th className="px-2 py-2 text-left font-bold border border-emerald-900 sticky z-30 bg-emerald-700" style={{ width: 170, left: 38 }}>EMPLOYEE</th>
                  {dayNumbers.map(d => (<th key={d} className="text-center font-bold border border-emerald-900" style={{ width: 28 }}>{d}</th>))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={dayNumbers.length + 2} className="py-8 text-center text-stone-400">Loading…</td></tr>}
                {!loading && employees.map((emp, i) => {
                  const days = attMap[emp.employeeId] || attMap[emp.employeeName] || {};
                  const rowBg = i % 2 === 0 ? '#ffffff' : '#fafaf9';
                  return (
                    <tr key={emp.employeeId || i}>
                      <td className="px-2 py-1 text-center font-semibold border border-stone-200 sticky left-0 z-10" style={{ backgroundColor: rowBg, width: 38 }}>{emp.srNo || i + 1}</td>
                      <td className="px-2 py-1 text-left font-bold border border-stone-200 sticky z-10 truncate" style={{ left: 38, backgroundColor: rowBg, width: 170, maxWidth: 170 }} title={emp.employeeName}>{emp.employeeName}</td>
                      {dayNumbers.map(d => {
                        const v = (days[d] || days[String(d)] || '').toUpperCase();
                        const sc = STATUS_COLORS[v];
                        return (
                          <td key={d} className="text-center border border-stone-200" style={{ width: 28, backgroundColor: sc ? sc.bg : '#fff', color: sc ? sc.fg : '#a8a29e', fontWeight: sc ? 700 : 400, fontSize: 10 }}>{v ? v[0] : ''}</td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
//  SUB-TAB 3: CALCULATION
// ─────────────────────────────────────────────────────────────────
const CalculationTab = () => {
  const { month, year, setMonth, setYear } = useMonthYear();
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [configDraft, setConfigDraft] = useState({});
  const token = localStorage.getItem('crm_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hr/payroll/calculation`, { params: { month, year }, headers });
      setRows(res.data.rows || []);
      setConfig(res.data.config || {});
      setConfigDraft(res.data.config || {});
    } catch {
      toast.error('Failed to load Calculation');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateField = async (employeeId, field, value) => {
    const numVal = field === 'remark' || field === 'bank' || field === 'accountNo' || field === 'ifsc' || field === 'project' || field === 'pfNo' || field === 'esiNo' || field === 'uan'
      ? null
      : Number(value);
    const body = {
      employeeId, month, year, field,
      ...(numVal !== null && !isNaN(numVal) ? { value: numVal } : { valueStr: String(value) }),
    };
    try {
      const res = await axios.patch(`${API}/hr/payroll/calculation`, body, { headers });
      setRows(prev => prev.map(r => (r.employeeId === employeeId ? res.data.row : r)));
    } catch {
      toast.error('Save failed');
      fetchData();
    }
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API}/hr/payroll/config`, { config: configDraft }, { headers });
      setConfig(configDraft);
      setShowConfig(false);
      toast.success('Formula config saved');
      fetchData();
    } catch {
      toast.error('Failed to save config');
    }
  };

  const EditableCell = ({ row, field, type = 'number', cls = 'text-right' }) => {
    const [val, setVal] = useState(row[field] ?? '');
    useEffect(() => { setVal(row[field] ?? ''); }, [row, field]);
    return (
      <td className={`px-1.5 py-1 border border-stone-200 ${cls}`}>
        <input
          type={type}
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={() => { if (String(val) !== String(row[field] ?? '')) updateField(row.employeeId, field, val); }}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
          className="w-full bg-transparent text-[11px] text-right focus:outline-none focus:bg-yellow-50 px-1 rounded"
          data-testid={`calc-${row.employeeId}-${field}`}
        />
      </td>
    );
  };

  return (
    <div data-testid="payroll-calculation-tab" className="space-y-3">
      <div className="bg-blue-800 text-white px-3 py-2 rounded-t font-bold text-sm flex items-center justify-between">
        <span>CALCULATION · {MONTHS[month - 1].toUpperCase()} {year}</span>
        <div className="flex items-center gap-2">
          <button data-testid="payroll-config-toggle" onClick={() => setShowConfig(!showConfig)} className="flex items-center gap-1 px-2 py-1 bg-blue-700 hover:bg-blue-600 rounded text-xs"><Settings2 className="w-3 h-3" /> Formulas</button>
          <MonthYearNav month={month} year={year} setMonth={setMonth} setYear={setYear} testidPrefix="payroll-calc" />
        </div>
      </div>

      {showConfig && (
        <Card>
          <CardContent className="p-3 bg-blue-50 border-blue-200">
            <h4 className="text-xs font-bold mb-2 text-blue-900">Formula Configuration (% of Basic / Gross)</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              {Object.keys(configDraft).map(k => (
                <div key={k} className="flex items-center gap-2">
                  <label className="font-semibold text-stone-700 truncate flex-1" title={k}>{k}</label>
                  <Input type="number" step="0.001" value={configDraft[k]} onChange={e => setConfigDraft(prev => ({ ...prev, [k]: parseFloat(e.target.value) || 0 }))} className="h-7 w-20 text-xs" data-testid={`config-${k}`} />
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-2">
              <Button size="sm" onClick={saveConfig} className="bg-blue-700 hover:bg-blue-800 text-white text-xs h-7" data-testid="payroll-config-save"><Save className="w-3 h-3 mr-1" /> Save & Recalc</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-t-none">
        <CardContent className="p-0">
          <div className="overflow-auto" style={{ maxHeight: 600 }}>
            <table className="text-[11px] border-collapse" style={{ minWidth: 2400 }}>
              <thead className="sticky top-0 z-20">
                <tr className="bg-blue-800 text-white">
                  <th className="px-2 py-2 border border-blue-900 sticky left-0 z-30 bg-blue-800" style={{ width: 38 }}>SR</th>
                  <th className="px-2 py-2 border border-blue-900 text-left sticky z-30 bg-blue-800" style={{ width: 170, left: 38 }}>NAME</th>
                  {['DESIG', 'GROSS *', 'BASIC', 'DA', 'HRA', 'SPECIAL', 'OTHER ALLOW *', 'INCENTIVE *', 'WORKING DAYS', 'PAY DAYS', 'PER DAY', 'EARNED GROSS', 'EMP PF', 'ESIC EMP', 'PT *', 'TDS *', 'SAL ADV *', 'OTHER DED *', 'CHARGES *', 'TOTAL DED', 'NET SALARY', 'EMPLOYER PF', 'ESIC EMPLOYER', 'MONTHLY CTC', 'YEARLY CTC', 'BANK *', 'A/C NO *', 'IFSC *', 'REMARK *'].map(h => (
                    <th key={h} className="px-2 py-2 border border-blue-900 whitespace-nowrap text-[10px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={31} className="py-8 text-center text-stone-400">Loading…</td></tr>}
                {!loading && rows.map((r, i) => (
                  <tr key={r.employeeId || i} className={i % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                    <td className="px-2 py-1 border border-stone-200 text-center font-semibold sticky left-0 z-10" style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafaf9' }}>{r.srNo}</td>
                    <td className="px-2 py-1 border border-stone-200 font-bold whitespace-nowrap sticky z-10" style={{ left: 38, backgroundColor: i % 2 === 0 ? '#fff' : '#fafaf9' }} title={r.employeeName}>{r.employeeName}</td>
                    <td className="px-2 py-1 border border-stone-200 text-stone-600 whitespace-nowrap">{r.designation || '—'}</td>
                    <EditableCell row={r} field="gross" />
                    <td className="px-2 py-1 border border-stone-200 text-right text-stone-600">{fmt(r.basic)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-stone-600">{fmt(r.da)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-stone-600">{fmt(r.hra)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-stone-600">{fmt(r.specialAllowance)}</td>
                    <EditableCell row={r} field="otherAllowance" />
                    <EditableCell row={r} field="incentive" />
                    <EditableCell row={r} field="workingDays" />
                    <EditableCell row={r} field="payDays" />
                    <td className="px-2 py-1 border border-stone-200 text-right text-stone-600">{fmt(r.perDay)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-stone-600">{fmt(r.earnedGross)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-red-700">{fmt(r.empPf)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-red-700">{fmt(r.esicEmp)}</td>
                    <EditableCell row={r} field="pt" cls="text-right text-red-700" />
                    <EditableCell row={r} field="tds" cls="text-right text-red-700" />
                    <EditableCell row={r} field="salaryAdvance" cls="text-right text-red-700" />
                    <EditableCell row={r} field="otherDeduction" cls="text-right text-red-700" />
                    <EditableCell row={r} field="charges" cls="text-right text-red-700" />
                    <td className="px-2 py-1 border border-stone-200 text-right font-bold text-red-800">{fmt(r.totalDeduction)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right font-bold text-emerald-700">{fmt(r.netSalary)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-purple-700">{fmt(r.employerPf)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-purple-700">{fmt(r.esicEmployer)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right font-bold text-purple-800">{fmt(r.monthlyCtc)}</td>
                    <td className="px-2 py-1 border border-stone-200 text-right text-stone-600">{fmt(r.yearlyCtc)}</td>
                    <EditableCell row={r} field="bank" type="text" cls="text-left" />
                    <EditableCell row={r} field="accountNo" type="text" cls="text-left" />
                    <EditableCell row={r} field="ifsc" type="text" cls="text-left" />
                    <EditableCell row={r} field="remark" type="text" cls="text-left" />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 text-[10px] text-stone-500 bg-blue-50 border-t border-blue-200">
            * = editable. Other columns auto-compute from Gross + Pay Days + Formula config. Changes save instantly.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
//  SUB-TAB 4: SALARY SLIP (auto-rendered + editable + printable)
// ─────────────────────────────────────────────────────────────────
const SalarySlipTab = () => {
  const { month, year, setMonth, setYear } = useMonthYear();
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [search, setSearch] = useState('');
  const [slip, setSlip] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('crm_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/hr/attendance-employees`, { headers })
      .then(r => setEmployees(r.data.employees || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search-by-name auto-resolve
  const matched = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.trim().toLowerCase();
    const exact = employees.find(e => (e.employeeName || '').toLowerCase() === q);
    if (exact) return exact;
    return employees.find(e => (e.employeeName || '').toLowerCase().includes(q));
  }, [search, employees]);

  useEffect(() => {
    if (matched && matched.employeeId !== employeeId) setEmployeeId(matched.employeeId);
  }, [matched, employeeId]);

  const fetchSlip = useCallback(async () => {
    if (!employeeId) { setSlip(null); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hr/payroll/slip/${employeeId}`, { params: { month, year }, headers });
      setSlip(res.data.slip);
    } catch {
      toast.error('Failed to fetch slip');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, month, year]);

  useEffect(() => { fetchSlip(); }, [fetchSlip]);

  const saveOverride = async (field, value) => {
    if (!employeeId) return;
    setSlip(prev => ({ ...prev, [field]: value }));
    try {
      await axios.put(`${API}/hr/payroll/slip`, {
        employeeId, month, year, overrides: { [field]: value },
      }, { headers });
      // refresh to get re-computed dependent fields
      fetchSlip();
    } catch {
      toast.error('Save failed');
    }
  };

  const printSlip = () => window.print();

  if (!slip && !loading) {
    return (
      <div data-testid="payroll-slip-tab" className="space-y-3">
        <div className="bg-purple-800 text-white px-3 py-2 rounded-t font-bold text-sm flex items-center justify-between">
          <span>SALARY SLIP · {MONTHS[month - 1].toUpperCase()} {year}</span>
          <div className="flex items-center gap-2">
            <Input data-testid="slip-employee-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Type employee name…" className="h-7 text-xs w-48 bg-white text-stone-900" />
            <select data-testid="slip-employee-select" value={employeeId} onChange={e => setEmployeeId(e.target.value)} className="text-xs border border-stone-300 rounded px-2 py-1 bg-white text-stone-900">
              <option value="">-- pick employee --</option>
              {employees.map(e => (<option key={e.employeeId} value={e.employeeId}>{e.employeeName}</option>))}
            </select>
            <MonthYearNav month={month} year={year} setMonth={setMonth} setYear={setYear} testidPrefix="payroll-slip" />
          </div>
        </div>
        <Card className="rounded-t-none"><CardContent className="py-12 text-center text-stone-400">
          <FileText className="w-10 h-10 mx-auto mb-3 text-stone-300" />
          <p className="font-semibold mb-1">Pick an employee or type their name</p>
          <p className="text-sm">e.g. type "Sahil" or pick from the dropdown above to view their salary slip.</p>
        </CardContent></Card>
      </div>
    );
  }

  const Row = ({ label, value, field, editable = true, type = 'number' }) => (
    <div className="flex items-center justify-between py-1 px-2 border-b border-stone-200">
      <span className="text-stone-600 text-[11px] font-medium">{label}</span>
      {editable && field ? (
        <input
          type={type}
          defaultValue={value ?? ''}
          onBlur={e => { const v = type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value; if (String(v) !== String(value ?? '')) saveOverride(field, v); }}
          className="text-[11px] text-right w-32 px-1 py-0.5 border border-stone-200 rounded focus:outline-none focus:border-purple-400 focus:bg-yellow-50"
          data-testid={`slip-${field}`}
        />
      ) : (
        <span className="text-[11px] font-bold text-stone-800">{type === 'number' ? `₹ ${fmt(value)}` : (value || '—')}</span>
      )}
    </div>
  );

  return (
    <div data-testid="payroll-slip-tab" className="space-y-3">
      <div className="bg-purple-800 text-white px-3 py-2 rounded-t font-bold text-sm flex items-center justify-between print:hidden">
        <span>SALARY SLIP · {MONTHS[month - 1].toUpperCase()} {year}</span>
        <div className="flex items-center gap-2">
          <Input data-testid="slip-employee-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Type employee name…" className="h-7 text-xs w-48 bg-white text-stone-900" />
          <select data-testid="slip-employee-select" value={employeeId} onChange={e => setEmployeeId(e.target.value)} className="text-xs border border-stone-300 rounded px-2 py-1 bg-white text-stone-900">
            {employees.map(e => (<option key={e.employeeId} value={e.employeeId}>{e.employeeName}</option>))}
          </select>
          <MonthYearNav month={month} year={year} setMonth={setMonth} setYear={setYear} testidPrefix="payroll-slip" />
          <Button size="sm" onClick={printSlip} className="bg-purple-700 hover:bg-purple-600 text-xs h-7" data-testid="slip-print-btn"><Printer className="w-3 h-3 mr-1" /> Print</Button>
        </div>
      </div>

      <Card className="rounded-t-none print:shadow-none print:rounded-none">
        <CardContent className="p-6 print:p-2 max-w-4xl mx-auto bg-white">
          {loading || !slip ? (
            <div className="py-12 text-center text-stone-400">Loading…</div>
          ) : (
            <>
              {/* Company header */}
              <div className="text-center mb-4 border-b-2 border-purple-800 pb-3">
                <h1 className="text-base font-bold text-purple-900">DBK GROUP OF INDUSTRIES</h1>
                <p className="text-[10px] text-stone-600">ASHTAVINAYAK INFRA OFFICE : B2-330A TO 334, 3RD FLOOR, B-WING, JAY GANESH VISION, AKURDI, PUNE 411035</p>
                <p className="text-sm font-bold mt-2">Pay Slip for {MONTHS[month - 1]} {year}</p>
              </div>

              {/* Two-column employee meta */}
              <div className="grid grid-cols-2 gap-x-6 mb-4 text-[11px]">
                <div>
                  <Row label="Employee ID" value={slip.employeeId} editable={false} type="text" />
                  <Row label="Employee Name" value={slip.employeeName} editable={false} type="text" />
                  <Row label="Designation" value={slip.designation} editable={false} type="text" />
                  <Row label="UAN" field="uan" value={slip.uan} type="text" />
                  <Row label="PF No." field="pfNo" value={slip.pfNo} type="text" />
                  <Row label="ESI No." field="esiNo" value={slip.esiNo} type="text" />
                  <Row label="Date of Joining" field="joiningDate" value={slip.joiningDate} type="text" />
                  <Row label="Bank" field="bank" value={slip.bank} type="text" />
                  <Row label="Account No." field="accountNo" value={slip.accountNo} type="text" />
                  <Row label="IFSC Code" field="ifsc" value={slip.ifsc} type="text" />
                </div>
                <div>
                  <Row label="Fixed Gross" field="gross" value={slip.gross} />
                  <Row label="Working Days" field="workingDays" value={slip.workingDays} />
                  <Row label="Pay Days" field="payDays" value={slip.payDays} />
                  <Row label="Per Day" value={slip.perDay} editable={false} />
                  <Row label="Earned Gross" value={slip.earnedGross} editable={false} />
                </div>
              </div>

              {/* Earnings + Deductions */}
              <div className="grid grid-cols-2 gap-x-6 border-t-2 border-purple-800 pt-3 mb-4">
                <div className="border border-stone-300 rounded">
                  <div className="bg-emerald-100 px-2 py-1.5 font-bold text-emerald-900 text-xs border-b border-stone-300">EARNINGS</div>
                  <Row label="Basic" value={slip.basic} editable={false} />
                  <Row label="HRA" value={slip.hra} editable={false} />
                  <Row label="Special Allowance" value={slip.specialAllowance} editable={false} />
                  <Row label="Other Allowance" field="otherAllowance" value={slip.otherAllowance} />
                  <Row label="Incentive" field="incentive" value={slip.incentive} />
                  <Row label="DA" value={slip.da} editable={false} />
                  <div className="flex items-center justify-between py-1.5 px-2 bg-emerald-50 border-t-2 border-emerald-300 font-bold">
                    <span className="text-emerald-900 text-[11px]">TOTAL EARNINGS</span>
                    <span className="text-emerald-900 text-[11px]">₹ {fmt(slip.earnedGross + (slip.otherAllowance || 0) + (slip.incentive || 0))}</span>
                  </div>
                </div>
                <div className="border border-stone-300 rounded">
                  <div className="bg-red-100 px-2 py-1.5 font-bold text-red-900 text-xs border-b border-stone-300">DEDUCTIONS</div>
                  <Row label="EPF" value={slip.empPf} editable={false} />
                  <Row label="ESI" value={slip.esicEmp} editable={false} />
                  <Row label="Professional Tax" field="pt" value={slip.pt} />
                  <Row label="TDS" field="tds" value={slip.tds} />
                  <Row label="Salary Advance" field="salaryAdvance" value={slip.salaryAdvance} />
                  <Row label="Other Deduction" field="otherDeduction" value={slip.otherDeduction} />
                  <Row label="Charges" field="charges" value={slip.charges} />
                  <div className="flex items-center justify-between py-1.5 px-2 bg-red-50 border-t-2 border-red-300 font-bold">
                    <span className="text-red-900 text-[11px]">TOTAL DEDUCTIONS</span>
                    <span className="text-red-900 text-[11px]">₹ {fmt(slip.totalDeduction)}</span>
                  </div>
                </div>
              </div>

              {/* Net salary */}
              <div className="bg-purple-100 border-2 border-purple-700 rounded p-3 flex items-center justify-between mb-4">
                <span className="text-purple-900 font-bold text-sm">NET SALARY</span>
                <span className="text-purple-900 font-extrabold text-xl" data-testid="slip-net-salary">₹ {fmt(slip.netSalary)}</span>
              </div>

              {/* Signatures */}
              <div className="grid grid-cols-2 gap-6 mt-8 text-[11px] text-stone-600">
                <div className="border-t border-stone-400 pt-2 text-center">Employee Signature</div>
                <div className="border-t border-stone-400 pt-2 text-center">HR Department Signature</div>
              </div>
              <p className="text-center text-[10px] text-stone-400 mt-3">This is a computer-generated salary slip.</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
//  ROOT — 4 sub-tabs
// ─────────────────────────────────────────────────────────────────
const SalaryPayrollTab = () => (
  <div data-testid="salary-payroll-tab">
    <Tabs defaultValue="index" className="space-y-3">
      <TabsList className="bg-stone-100" data-testid="salary-payroll-subtabs">
        <TabsTrigger value="index" data-testid="payroll-tab-index" className="data-[state=active]:bg-white"><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Index 1</TabsTrigger>
        <TabsTrigger value="presenty" data-testid="payroll-tab-presenty" className="data-[state=active]:bg-white"><ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Presenty</TabsTrigger>
        <TabsTrigger value="calculation" data-testid="payroll-tab-calculation" className="data-[state=active]:bg-white"><Calculator className="w-3.5 h-3.5 mr-1.5" /> Calculation</TabsTrigger>
        <TabsTrigger value="slip" data-testid="payroll-tab-slip" className="data-[state=active]:bg-white"><FileText className="w-3.5 h-3.5 mr-1.5" /> Salary Slip</TabsTrigger>
      </TabsList>
      <TabsContent value="index"><IndexTab /></TabsContent>
      <TabsContent value="presenty"><PresentyTab /></TabsContent>
      <TabsContent value="calculation"><CalculationTab /></TabsContent>
      <TabsContent value="slip"><SalarySlipTab /></TabsContent>
    </Tabs>

    <style>{`
      @media print {
        body * { visibility: hidden; }
        [data-testid="payroll-slip-tab"], [data-testid="payroll-slip-tab"] * { visibility: visible; }
        [data-testid="payroll-slip-tab"] { position: absolute; left: 0; top: 0; width: 100%; }
        [data-testid="payroll-slip-tab"] .print\\:hidden { display: none !important; }
      }
    `}</style>
  </div>
);

export default SalaryPayrollTab;
